import type { BetaSignupRequest } from '@rbf/shared';
import { Link } from 'expo-router';
import { useState } from 'react';
import { Pressable, Text as RNText } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Form } from '@/components/ui/Form';
import { PasswordField } from '@/components/ui/PasswordField';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_PLACEHOLDER,
  USERNAME_MAX_LENGTH,
} from '@/lib/constants';
import { supabase } from '@/lib/supabase';
import { useInFlightGuard } from '@/lib/useInFlightGuard';

export function SignUpForm() {
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runExclusive = useInFlightGuard();

  async function submit() {
    await runExclusive(async () => {
      const name = username.trim();
      if (!name) {
        setError('Choose a username.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        // Open sign-up is disabled server-side; account creation goes through the
        // invite-code-gated beta-signup function, then we sign in to get a session.
        const body: BetaSignupRequest = { code, email, password, displayName: name };
        const { error: fnError } = await supabase.functions.invoke('beta-signup', {
          body,
        });
        if (fnError) {
          let message: string | undefined;
          if ('context' in fnError) {
            const body = await (fnError.context as Response)
              .clone()
              .json()
              .catch(() => null);
            message = body?.error;
          }
          setError(message ?? 'Sign-up failed. Please try again.');
          return;
        }
        const { error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (authError) setError(authError.message);
      } catch {
        // A thrown transport error (vs a returned { error }) would otherwise leave
        // the button spinning forever; `finally` always clears busy.
        setError('Sign-up failed. Please try again.');
      } finally {
        setBusy(false);
      }
    });
  }

  return (
    <Form onSubmit={submit} className="gap-3">
      <TextField
        label="Invite code"
        value={code}
        onChangeText={setCode}
        placeholder="Your beta invite code"
        autoCapitalize="none"
        autoComplete="off"
        importantForAutofill="no"
        helper="Private beta — sign-up requires an invite code."
      />
      <TextField
        label="Username"
        value={username}
        onChangeText={setUsername}
        placeholder="Pick a username"
        maxLength={USERNAME_MAX_LENGTH}
        autoCapitalize="none"
        // Display name only — login is by email, so keep this out of the
        // credential the password manager saves (see the email field below).
        autoComplete="off"
        textContentType="nickname"
        importantForAutofill="no"
        helper={`Shown throughout the app · up to ${USERNAME_MAX_LENGTH} characters`}
      />
      <TextField
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="you@example.com"
        autoCapitalize="none"
        autoComplete="email"
        textContentType="username"
        importantForAutofill="yes"
        keyboardType="email-address"
        inputMode="email"
      />
      <PasswordField
        label="Password"
        value={password}
        onChangeText={setPassword}
        placeholder={PASSWORD_PLACEHOLDER}
        autoComplete="new-password"
        textContentType="newPassword"
        importantForAutofill="yes"
        helper={`Use ${MIN_PASSWORD_LENGTH} or more characters.`}
        showStrength
      />

      {error ? (
        <Text variant="caption" className="text-alert">
          {error}
        </Text>
      ) : null}

      <Button block submit label="Create account" busy={busy} />

      <Text variant="caption" className="text-center text-faint">
        By continuing you agree to our{' '}
        <Link href="/legal/terms" asChild>
          <RNText className="font-sans-semibold text-sm text-muted">Terms</RNText>
        </Link>{' '}
        and{' '}
        <Link href="/legal/privacy" asChild>
          <RNText className="font-sans-semibold text-sm text-muted">
            Privacy Policy
          </RNText>
        </Link>
        .
      </Text>

      <Link href="/sign-in" asChild>
        <Pressable className="self-center">
          <Text variant="caption" className="text-muted">
            Have an account? Sign in
          </Text>
        </Pressable>
      </Link>
    </Form>
  );
}
